<script setup>
/**
 * The details the assistant fills in — one row per `{{placeholder}}` in the address or in
 * what is sent.
 *
 * "PARAMETER" IS NOT TRANSLATED HERE, IT IS REPLACED. To an owner these are not function
 * arguments; they are the blanks in a sentence her assistant has to fill in before it can
 * ask the other system anything. So the section is "Details it fills in", each row's first
 * field is the name that appears in the address, and "required" is "must have it" — the
 * same substitution `TaskCollectList` makes, for the same reason.
 *
 * The type picker is a `<select>` rather than three radio cards: it is four values, it is
 * the fifth-most important control in the row, and a native select follows the theme
 * because `color-scheme` is on the host document. It carries no user text, so R7 does not
 * reach it.
 *
 * Rows carry ids and are keyed on them, never on the index — `task-draft.js` writes the
 * long version of why, and it is the same bug here: an index key makes Vue patch the third
 * box's VALUE while the DOM node stays put, so the caret jumps and a focused field can end
 * up holding its neighbour's text.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import {
  CONTACT_PLACEHOLDERS,
  MAX_PARAMS,
  PARAM_TYPES,
  paramRow,
} from '../helpers/connection-draft.js';

const props = defineProps({
  /** `[{ id, name, type, description, required }]`. */
  modelValue: { type: Array, default: () => [] },
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.DETAILS.${name}`;

const isFull = computed(() => props.modelValue.length >= MAX_PARAMS);

const typeOptions = computed(() =>
  PARAM_TYPES.map(type => ({
    value: type,
    label: t(`${key('TYPES')}.${type.toUpperCase()}`),
  }))
);

const patchAt = (index, patch) =>
  emit(
    'update:modelValue',
    props.modelValue.map((row, at) =>
      at === index ? { ...row, ...patch } : row
    )
  );

const removeAt = index =>
  emit(
    'update:modelValue',
    props.modelValue.filter((row, at) => at !== index)
  );

const add = () => emit('update:modelValue', [...props.modelValue, paramRow()]);

/**
 * The four the SERVER fills in, listed so the owner can find them.
 *
 * They live at the BOTTOM of this section, under the rows and the Add button, because they
 * are not a step in the task — nothing has to be done about them. Putting them at the top
 * would make the first thing an owner reads in "Details it fills in" a list of details she
 * does NOT fill in.
 *
 * Rendered from the helper rather than from a translated string: these are literal tokens
 * she copies, so a translator must never be in a position to localize one.
 */
const contactPlaceholders = CONTACT_PLACEHOLDERS.map(name => `{{${name}}}`);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-connection-params>
    <p
      v-if="modelValue.length === 0"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-connection-params-none
    >
      {{ t(key('NONE')) }}
    </p>

    <div
      v-for="(row, index) in modelValue"
      :key="row.id"
      class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
      data-waslo-connection-param
    >
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
        <WasloInput
          class="sm:w-52"
          :model-value="row.name"
          :label="t(key('NAME'))"
          :placeholder="t(key('NAME_PLACEHOLDER'))"
          data-waslo-connection-param-name
          @update:model-value="value => patchAt(index, { name: value })"
        />
        <WasloSelect
          class="sm:w-44"
          :model-value="row.type"
          :label="t(key('TYPE'))"
          :options="typeOptions"
          data-waslo-connection-param-type
          @update:model-value="value => patchAt(index, { type: value })"
        />
        <!-- `h-10` matches the input's own height, so the switch and the bin sit on the
             field's centre line rather than on its label's. -->
        <div class="flex items-center h-10 gap-3 shrink-0 sm:ms-auto ms-0">
          <span class="text-sm text-n-slate-11">{{ t(key('REQUIRED')) }}</span>
          <Switch
            :model-value="row.required"
            :aria-label="t(key('REQUIRED'))"
            data-waslo-connection-param-required
            @update:model-value="value => patchAt(index, { required: value })"
          />
          <button
            type="button"
            class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-ruby-9 hover:bg-n-alpha-2"
            :aria-label="t(key('REMOVE'))"
            :title="t(key('REMOVE'))"
            data-waslo-connection-param-remove
            @click="removeAt(index)"
          >
            <span class="size-4 i-lucide-trash-2" aria-hidden="true" />
          </button>
        </div>
      </div>
      <WasloInput
        :model-value="row.description"
        :label="t(key('NOTE'))"
        :placeholder="t(key('NOTE_PLACEHOLDER'))"
        @update:model-value="value => patchAt(index, { description: value })"
      />
    </div>

    <div
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-connection-contact-details
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('CONTACT_HEADING')) }}
      </p>
      <!-- `dir="ltr"` per token: the names are literal ASCII the owner copies, and an
           Arabic paragraph would otherwise lay `{{contact.phone_number}}` out backwards. -->
      <ul class="flex flex-wrap gap-2 p-0 m-0 list-none">
        <li
          v-for="token in contactPlaceholders"
          :key="token"
          dir="ltr"
          class="px-2 py-1 font-mono text-xs rounded-md text-n-slate-12 bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-weak"
        >
          {{ token }}
        </li>
      </ul>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t(key('CONTACT_DESCRIPTION')) }}
      </p>
    </div>

    <p
      v-if="message"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-connection-params-error
    >
      {{ message }}
    </p>

    <div class="flex items-center gap-3">
      <Button
        :label="t(key('ADD'))"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="isFull"
        data-waslo-connection-param-add
        @click="add"
      />
      <span v-if="isFull" class="text-sm text-n-slate-11">
        {{ t(key('FULL')) }}
      </span>
    </div>
  </div>
</template>
